'use client'

// A frame-rate readout for A/B judgements, drawn with ?fps=1.
//
// Two numbers because averages lie about stutter: the rolling average
// says how it mostly runs, the 1% low (the mean of the worst percentile
// of recent frames, read as fps) says how it hitches. A post effect that
// holds the average but wrecks the low is a fail, and without the second
// number that fail is invisible in a meter.
//
// window.__fps carries { avg, low } so an A/B can be scripted rather than
// eyeballed. Dev tool: no styling kit, no store, no place in the layout.

import { useEffect, useRef, useState } from 'react'

const WINDOW_MS = 2000

export function FpsMeter() {
  const [on, setOn] = useState(false)
  const [read, setRead] = useState({ avg: 0, low: 0 })
  const frames = useRef([])

  useEffect(() => {
    if (new URLSearchParams(location.search).get('fps') !== '1') return
    // Deferred a tick: the meter appearing is not worth a cascading
    // render warning, and nothing reads it in its first millisecond.
    const armed = setTimeout(() => setOn(true), 0)
    let raf
    let last = performance.now()
    const tick = (now) => {
      frames.current.push({ t: now, dt: now - last })
      last = now
      const cutoff = now - WINDOW_MS
      while (frames.current.length && frames.current[0].t < cutoff) frames.current.shift()
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    const report = setInterval(() => {
      const dts = frames.current.map((f) => f.dt).filter((d) => d > 0)
      if (dts.length < 10) return
      const avg = 1000 / (dts.reduce((a, b) => a + b, 0) / dts.length)
      const worst = [...dts].sort((a, b) => b - a)
      const n = Math.max(1, Math.floor(worst.length * 0.01))
      const low = 1000 / (worst.slice(0, n).reduce((a, b) => a + b, 0) / n)
      const out = { avg: Math.round(avg), low: Math.round(low) }
      setRead(out)
      window.__fps = out
    }, 500)

    return () => { clearTimeout(armed); cancelAnimationFrame(raf); clearInterval(report); delete window.__fps }
  }, [])

  if (!on) return null
  return (
    <div
      style={{
        position: 'fixed', left: 10, top: 10, zIndex: 200, pointerEvents: 'none',
        background: 'rgba(0,0,0,0.72)', color: '#9be89b', padding: '6px 10px',
        borderRadius: 8, fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.5,
      }}
    >
      {read.avg} fps avg<br />{read.low} fps 1% low
    </div>
  )
}

export default FpsMeter
